/* ===== scroll-reveal.js ===== */
;/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Global Scroll Reveal
   ─────────────────────────────────────────────────────────────────────────────

   HOW TO USE IN ANY SECTION
   ─────────────────────────
   Single element (animates independently):
     <h2 data-reveal>Heading</h2>
     <p  data-reveal data-reveal-delay="0.12">Body copy</p>

   Direction (default = "up"):
     data-reveal="up"    — slides in from below   (exit: slides up-left)
     data-reveal="left"  — slides in from right
     data-reveal="right" — slides in from left
     data-reveal="fade"  — opacity only, no movement

   Staggered group (children fire sequentially as a batch):
     <div data-reveal-group data-reveal-stagger="0.1">
       <span data-reveal-child>Item 1</span>
       <span data-reveal-child>Item 2</span>
     </div>

   Options:
     data-reveal-delay="0.15"    extra seconds before animation fires
     data-reveal-duration="0.7"  animation speed in seconds
     data-reveal-once            prevent exit / re-entry animation (stays visible)
     data-reveal-start="top 80%" custom ScrollTrigger start position

   WHAT IT DOES
   ────────────
   • Enter  → element slides in + fades to full opacity
   • Exit   → element slides up-left + fades out (keeping the page feeling alive)
   • EnterBack → re-animates from below if you scroll back up
   • LeaveBack → re-hides if you scroll back past the entry point

   Exit animation is on by default. Add data-reveal-once to any element
   you want to stay permanently visible after first reveal.
──────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var GSAP_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
  var ST_CDN   = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';

  var CFG = {
    duration:     0.72,
    ease:         'power3.out',
    exitEase:     'power2.in',
    exitDuration: 0.42,
    distance:     48,
    stagger:      0.10,
    start:        'top 88%'
  };

  /* ── Build the "from" state based on direction ── */
  function fromState(el) {
    var dir  = ((el.dataset.reveal || el.dataset.revealDir || 'up')).toLowerCase();
    var dist = parseFloat(el.dataset.revealDistance) || CFG.distance;
    var from = { opacity: 0 };
    if (dir === 'up')    from.y = dist;
    if (dir === 'down')  from.y = -dist;
    if (dir === 'left')  from.x = dist;
    if (dir === 'right') from.x = -dist;
    return from;
  }

  /* ── Enter tween for a single element ── */
  function enterTween(el, delay) {
    return {
      opacity:  1,
      x:        0,
      y:        0,
      duration: parseFloat(el.dataset.revealDuration) || CFG.duration,
      ease:     CFG.ease,
      delay:    delay || 0
    };
  }

  /* ── Wire up a single [data-reveal] element ── */
  function revealEl(el) {
    if (el._srInit) return;
    el._srInit = true;

    var once  = el.hasAttribute('data-reveal-once');
    var delay = parseFloat(el.dataset.revealDelay) || 0;

    gsap.set(el, fromState(el));

    ScrollTrigger.create({
      trigger: el,
      start:   el.dataset.revealStart || CFG.start,

      onEnter: function () {
        gsap.to(el, enterTween(el, delay));
      },

      onLeave: once ? undefined : function () {
        /* Exit upward + slight left drift — the "Zera" feel */
        gsap.to(el, {
          opacity:  0,
          y:        -22,
          x:        -18,
          duration: CFG.exitDuration,
          ease:     CFG.exitEase
        });
      },

      onEnterBack: once ? undefined : function () {
        gsap.to(el, enterTween(el, 0));
      },

      onLeaveBack: once ? undefined : function () {
        /* Re-hide below when scrolling back above the trigger */
        var from = fromState(el);
        gsap.to(el, {
          opacity:  0,
          y:        from.y || 0,
          x:        from.x || 0,
          duration: CFG.exitDuration,
          ease:     CFG.exitEase
        });
      }
    });
  }

  /* ── Wire up a [data-reveal-group] — children stagger as one batch ── */
  function revealGroup(groupEl) {
    if (groupEl._srInit) return;
    groupEl._srInit = true;

    var once    = groupEl.hasAttribute('data-reveal-once');
    var stagger = parseFloat(groupEl.dataset.revealStagger) || CFG.stagger;
    var children = Array.prototype.slice.call(
      groupEl.querySelectorAll('[data-reveal-child]')
    );
    if (!children.length) return;

    /* Set initial hidden state on each child */
    children.forEach(function (child) {
      gsap.set(child, fromState(child));
    });

    ScrollTrigger.create({
      trigger: groupEl,
      start:   groupEl.dataset.revealStart || CFG.start,

      onEnter: function () {
        gsap.to(children, {
          opacity:  1, x: 0, y: 0,
          duration: CFG.duration,
          ease:     CFG.ease,
          stagger:  stagger
        });
      },

      onLeave: once ? undefined : function () {
        gsap.to(children, {
          opacity:  0, y: -18, x: -14,
          duration: CFG.exitDuration,
          ease:     CFG.exitEase,
          stagger:  0.05
        });
      },

      onEnterBack: once ? undefined : function () {
        gsap.to(children, {
          opacity:  1, x: 0, y: 0,
          duration: CFG.duration,
          ease:     CFG.ease,
          stagger:  { each: stagger, from: 'end' }
        });
      },

      onLeaveBack: once ? undefined : function () {
        var reverseChildren = children.slice().reverse();
        gsap.to(reverseChildren, {
          opacity:  0,
          y:        function (i, el) { return fromState(el).y || 0; },
          x:        function (i, el) { return fromState(el).x || 0; },
          duration: CFG.exitDuration,
          ease:     CFG.exitEase,
          stagger:  0.05
        });
      }
    });
  }

  /* ── Scan the DOM for reveal elements ── */
  function init(root) {
    root = root || document;

    /* Groups first */
    root.querySelectorAll('[data-reveal-group]').forEach(function (g) {
      revealGroup(g);
    });

    /* Standalone elements (skip children already handled by a group) */
    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      if (el.closest('[data-reveal-group]')) return;
      revealEl(el);
    });
  }

  /* ── GSAP loader (deduplicates via script tag check) ── */
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        /* Already injected — may still be loading */
        if (window.gsap && window.ScrollTrigger) { resolve(); return; }
        var existing = document.querySelector('script[src="' + src + '"]');
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', reject);
        return;
      }
      var s = document.createElement('script');
      s.src     = src;
      s.onload  = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function boot(root) {
    if (window.gsap && window.ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      init(root);
      return;
    }
    loadScript(GSAP_CDN)
      .then(function () { return loadScript(ST_CDN); })
      .then(function () {
        gsap.registerPlugin(ScrollTrigger);
        init(root);
      })
      .catch(function (e) { console.warn('[scroll-reveal] GSAP load failed:', e); });
  }

  /* ── Startup ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Shopify editor: re-run when any section is added or reloaded */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });

  /* Expose globally so other scripts can call ScrollReveal.refresh() */
  window.ScrollReveal = {
    refresh: function (root) { boot(root || document); }
  };
})();

;
/* ===== parallax.js ===== */
;/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Global Parallax Engine
   ─────────────────────────────────────────────────────────────────────────────

   HOW TO USE
   ──────────
   Add data-parallax to any section wrapper that contains a full-bleed
   background image. The engine finds the img/video inside, scales it up
   slightly, and translates it on scroll — creating a floating depth effect.

   Basic:
     <div data-parallax> ... <img class="my-bg-image"> ... </div>

   Custom speed (0.1 = subtle, 0.3 = strong, default 0.2):
     <div data-parallax data-parallax-speed="0.15"> ... </div>

   Target a specific child image by class:
     <div data-parallax data-parallax-target=".my-specific-img"> ... </div>

   REQUIREMENTS ON THE CONTAINER
   ──────────────────────────────
   The container must have overflow: hidden (the engine sets this automatically).
   The target image must be position: absolute (the engine sets this too).

   The engine scales the image to 120% height and re-centres it so there is
   always visual buffer above and below — no white gaps on any screen size.

   REDUCED MOTION
   ──────────────
   Fully respects prefers-reduced-motion — parallax is disabled, image
   reverts to normal object-fit: cover fill.
──────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var items = [];     // { el, img, speed }
  var rafId = null;
  var ticking = false;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Set up one parallax item ── */
  function setup(container) {
    if (container._parallaxInit) return;
    container._parallaxInit = true;

    var targetSel = container.dataset.parallaxTarget;
    var img = targetSel
      ? container.querySelector(targetSel)
      : container.querySelector('img, video');

    if (!img) return;

    var speed = parseFloat(container.dataset.parallaxSpeed) || 0.2;

    if (REDUCED_MOTION) {
      /* No animation — ensure image still fills container */
      img.style.height = '100%';
      img.style.top    = '0';
      return;
    }

    /* Container needs overflow: hidden for clipping */
    container.style.overflow = 'hidden';

    /* Scale image so there's room to shift without gaps.
       extra = speed * 100 (e.g. 0.2 speed → 20% extra height, 10% top/bottom buffer) */
    var extra   = Math.round(speed * 100);
    var overage = Math.round(extra / 2);

    img.style.position      = 'absolute';
    img.style.top           = '-' + overage + '%';
    img.style.left          = '0';
    img.style.width         = '100%';
    img.style.height        = (100 + extra) + '%';
    img.style.objectFit     = 'cover';
    img.style.willChange    = 'transform';
    img.style.transform     = 'translateY(0px)';
    img.style.pointerEvents = 'none';

    items.push({ el: container, img: img, speed: speed, extra: extra });
  }

  /* ── Calculate and apply transforms ── */
  function updateAll() {
    var viewH = window.innerHeight;

    items.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();

      /* Skip if completely off-screen */
      if (rect.bottom < -100 || rect.top > viewH + 100) return;

      /* Progress: 0 when element bottom is at viewport bottom,
                   1 when element top is at viewport top */
      var totalTravel = viewH + rect.height;
      var scrolled    = viewH - rect.top;
      var progress    = Math.min(1, Math.max(0, scrolled / totalTravel));

      /* Offset: negative = image up (parallax effect as section enters from below),
                 positive = image down (section scrolling off top) */
      var maxShift = (item.extra / 100) * rect.height * 0.5;
      var offset   = (progress - 0.5) * maxShift * 2 * -1;

      item.img.style.transform = 'translateY(' + offset.toFixed(2) + 'px)';
    });

    ticking = false;
  }

  /* ── Scroll listener with RAF throttle ── */
  function onScroll() {
    if (ticking) return;
    ticking = true;
    rafId = requestAnimationFrame(updateAll);
  }

  /* ── Init: scan for [data-parallax] containers ── */
  function init(root) {
    root = root || document;
    root.querySelectorAll('[data-parallax]').forEach(function (el) {
      setup(el);
    });
    /* Also auto-detect image-heading sections (they have inline bg images) */
    root.querySelectorAll('.image-heading-section').forEach(function (el) {
      if (!el.hasAttribute('data-parallax')) {
        el.setAttribute('data-parallax', '');
        el.dataset.parallaxSpeed = '0.18';
        setup(el);
      }
    });

    if (items.length && !REDUCED_MOTION) {
      window.addEventListener('scroll', onScroll, { passive: true });
      updateAll(); /* Set initial position */
    }
  }

  /* ── Startup ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  window.ParallaxEngine = { refresh: init };
})();

;
/* ===== section-stack.js ===== */
;/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Section Sticky Stacking
   ─────────────────────────────────────────────────────────────────────────────

   HOW TO USE
   ──────────
   Add data-stack to any section's root element. Consecutive data-stack
   sections automatically stack on top of each other as you scroll — each new
   section slides over the previous one like a card being dealt.

   In a section's Liquid file, on the outermost <div>:
     <div data-stack {{ section.shopify_attributes }}>

   Optional attributes:
     data-stack-radius="20"    corner radius in px (default 16)
     data-stack-scale="0.95"   scale of buried section (default 0.96)
     data-stack-shadow         add a subtle drop shadow to each card

   BEHAVIOUR
   ─────────
   • Each section gets position: sticky; top: 0 so it stays pinned
   • Z-index increments so later sections slide over earlier ones
   • As each new section enters, the buried one scales down slightly
   • Produces a "card deck" depth effect without any JS frameworks
   • Respects prefers-reduced-motion (disables scale animation only)

   TO REMOVE STACKING FROM ONE SECTION
   ────────────────────────────────────
   Remove data-stack from its root element, or add data-stack-skip.
   The engine only groups consecutive data-stack elements.
──────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Collect consecutive runs of [data-stack] siblings ── */
  function findGroups() {
    var all    = Array.prototype.slice.call(document.querySelectorAll('[data-stack]'));
    var groups = [];
    var group  = [];

    all.forEach(function (el, i) {
      if (group.length === 0) {
        group.push(el);
      } else {
        /* Check if this element immediately follows the last (same parent, adjacent) */
        var prev = group[group.length - 1];
        var isAdjacent = prev.parentElement === el.parentElement &&
          prev.nextElementSibling === el;
        if (isAdjacent) {
          group.push(el);
        } else {
          if (group.length > 1) groups.push(group);
          group = [el];
        }
      }
    });
    if (group.length > 1) groups.push(group);
    return groups;
  }

  /* ── Apply stacking styles to one group ── */
  function applyGroup(items) {
    var BASE_Z = 10;

    items.forEach(function (el, i) {
      var radius    = parseFloat(el.dataset.stackRadius)   || 16;
      var hasShadow = el.hasAttribute('data-stack-shadow');

      /* Sticky positioning */
      el.style.position      = 'sticky';
      el.style.top           = '0';
      el.style.zIndex        = BASE_Z + i;
      el.style.transformOrigin = 'top center';

      /* Card styling */
      el.style.borderTopLeftRadius  = radius + 'px';
      el.style.borderTopRightRadius = radius + 'px';
      el.style.overflow = 'hidden';

      if (hasShadow) {
        el.style.boxShadow = '0 -4px 32px rgba(0,0,0,0.14)';
      }
    });

    if (REDUCED_MOTION) return; /* Skip scale animation */

    /* ── Scroll-driven scale effect ── */
    var ticking = false;

    function update() {
      var viewH = window.innerHeight;

      items.forEach(function (el, i) {
        /* Only scale sections that have something coming over them */
        if (i >= items.length - 1) return;

        var next     = items[i + 1];
        var nextTop  = next.getBoundingClientRect().top;
        var maxScale = parseFloat(el.dataset.stackScale) || 0.96;

        /* Progress: 0 when next section hasn't entered, 1 when it's fully over */
        var progress = Math.max(0, Math.min(1, 1 - (nextTop / viewH)));
        var scale    = 1 - (progress * (1 - maxScale));

        el.style.transform = 'scale(' + scale.toFixed(4) + ')';
      });

      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });

    update(); /* Set initial state */
  }

  /* ── Main init ── */
  function init() {
    var groups = findGroups();
    groups.forEach(applyGroup);
  }

  /* ── Startup ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-run on Shopify editor section add/remove */
  document.addEventListener('shopify:section:load',   init);
  document.addEventListener('shopify:section:unload', init);

  window.SectionStack = { refresh: init };
})();

;
/* ===== card-tilt.js ===== */
;/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Card 3D Tilt on Hover
   ─────────────────────────────────────────────────────────────────────────────

   HOW TO USE
   ──────────
   Add data-tilt to any card element. The card will tilt toward the cursor
   as you hover, creating a physical depth effect.

     <div class="my-card" data-tilt>...</div>

   Options (all optional):
     data-tilt-max="8"        max tilt angle in degrees (default 7)
     data-tilt-scale="1.03"   scale on hover (default 1.03)
     data-tilt-speed="400"    return-to-flat transition in ms (default 500)

   Automatically disabled for prefers-reduced-motion users.
──────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var PERSPECTIVE  = 900;   /* px — higher = flatter perspective */
  var DEFAULT_MAX  = 7;     /* degrees */
  var DEFAULT_SCALE = 1.03;
  var EASE_IN  = 'transform 0.12s ease';
  var EASE_OUT = 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)';

  function setup(el) {
    if (el._tiltInit) return;
    el._tiltInit = true;

    var maxTilt = parseFloat(el.dataset.tiltMax)   || DEFAULT_MAX;
    var scale   = parseFloat(el.dataset.tiltScale)  || DEFAULT_SCALE;
    var raf     = null;
    var rotX    = 0;
    var rotY    = 0;

    el.style.willChange = 'transform';

    el.addEventListener('mousemove', function (e) {
      var rect = el.getBoundingClientRect();
      /* Normalise cursor to -0.5 … +0.5 within the card */
      var nx = (e.clientX - rect.left)  / rect.width  - 0.5;
      var ny = (e.clientY - rect.top)   / rect.height - 0.5;
      rotY   =  nx * maxTilt * 2;   /* left/right tilt */
      rotX   = -ny * maxTilt * 2;   /* up/down tilt (inverted) */

      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        el.style.transition = EASE_IN;
        el.style.transform  =
          'perspective(' + PERSPECTIVE + 'px) ' +
          'rotateX('     + rotX.toFixed(2) + 'deg) ' +
          'rotateY('     + rotY.toFixed(2) + 'deg) ' +
          'scale('       + scale + ')';
      });
    });

    el.addEventListener('mouseleave', function () {
      if (raf) { cancelAnimationFrame(raf); raf = null; }
      el.style.transition = EASE_OUT;
      el.style.transform  =
        'perspective(' + PERSPECTIVE + 'px) rotateX(0deg) rotateY(0deg) scale(1)';
    });
  }

  function init(root) {
    root = root || document;
    root.querySelectorAll('[data-tilt]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  window.CardTilt = { refresh: init };
})();

;
/* ===== image-reveal.js ===== */
;/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Image Reveal Sweep
   ─────────────────────────────────────────────────────────────────────────────

   HOW TO USE
   ──────────
   Add data-reveal-sweep to any element that wraps an <img>.
   A coloured bar sweeps in from the left to cover the image, then exits to
   the right — revealing the image beneath.

     <div class="my-image-wrapper" data-reveal-sweep>
       <img src="..." />
     </div>

   Options (all optional):
     data-reveal-sweep-color="#111111"   bar colour (default #111111)
     data-reveal-sweep-delay="200"       delay before sweep starts, in ms

   Automatically disabled for prefers-reduced-motion users.
   ─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var ATTR          = 'data-reveal-sweep';
  var COLOR_ATTR    = 'data-reveal-sweep-color';
  var DELAY_ATTR    = 'data-reveal-sweep-delay';
  var DEFAULT_COLOR = '#111111';
  var PHASE1        = 380;   /* ms — bar sweeps in */
  var PAUSE         = 30;    /* ms — gap between phases */
  var PHASE2        = 380;   /* ms — bar exits */
  var EASE          = 'cubic-bezier(0.77, 0, 0.18, 1)';

  /* ── One-time global styles ────────────────────────────────────────────── */
  function injectStyles() {
    if (document.getElementById('pga-image-reveal-styles')) return;
    var s = document.createElement('style');
    s.id = 'pga-image-reveal-styles';
    s.textContent = [
      '/* image-reveal.js */                    ',
      '[data-reveal-sweep] {                     ',
      '  position: relative;                     ',
      '  overflow: hidden;                       ',
      '}                                         ',
      '[data-reveal-sweep] img {                 ',
      '  opacity: 0;                             ',
      '  transition: opacity 0s;                 ',
      '}                                         ',
      '[data-reveal-sweep].ir-done img {         ',
      '  opacity: 1;                             ',
      '}                                         ',
      '.ir-bar {                                 ',
      '  position: absolute;                     ',
      '  inset: 0;                               ',
      '  z-index: 2;                             ',
      '  pointer-events: none;                   ',
      '  transform: scaleX(0);                   ',
      '  transform-origin: left center;          ',
      '}                                         ',
    ].join('\n');
    document.head.appendChild(s);
  }

  /* ── Animate one element ───────────────────────────────────────────────── */
  function animateEl(el) {
    if (el._irDone) return;
    el._irDone = true;

    var color  = el.getAttribute(COLOR_ATTR) || DEFAULT_COLOR;
    var delay  = parseInt(el.getAttribute(DELAY_ATTR), 10) || 0;

    var bar = document.createElement('div');
    bar.className = 'ir-bar';
    bar.style.background  = color;
    bar.style.transition  = 'transform 0s';
    el.appendChild(bar);

    /* Two rAF calls ensure the initial state is painted before we transition */
    function startSweep() {
      /* ── Phase 1: bar sweeps IN from the left ─── */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          bar.style.transition     = 'transform ' + PHASE1 + 'ms ' + EASE;
          bar.style.transformOrigin = 'left center';
          bar.style.transform      = 'scaleX(1)';

          setTimeout(function () {
            /* Reveal image now (hidden behind full bar, so change is invisible) */
            el.classList.add('ir-done');

            /* ── Phase 2: bar exits to the right ─── */
            bar.style.transition     = 'transform ' + PHASE2 + 'ms ' + EASE;
            bar.style.transformOrigin = 'right center';

            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                bar.style.transform = 'scaleX(0)';

                setTimeout(function () {
                  /* Clean up — bar no longer needed */
                  if (bar.parentNode) bar.parentNode.removeChild(bar);
                }, PHASE2 + 60);
              });
            });
          }, PHASE1 + PAUSE);
        });
      });
    }

    if (delay > 0) {
      setTimeout(startSweep, delay);
    } else {
      startSweep();
    }
  }

  /* ── Observe + trigger ─────────────────────────────────────────────────── */
  function init(root) {
    root = root || document;
    var els = root.querySelectorAll('[' + ATTR + ']');
    if (!els.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          animateEl(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    Array.prototype.forEach.call(els, function (el) {
      observer.observe(el);
    });
  }

  /* ── Boot ──────────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { injectStyles(); init(); });
  } else {
    injectStyles();
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  window.ImageReveal = { refresh: init };
})();

;
/* ===== magnetic-hover.js ===== */
;/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Magnetic Hover on Buttons
   ─────────────────────────────────────────────────────────────────────────────

   Buttons subtly translate toward the cursor while hovered, then spring back
   when the cursor leaves. Works on any element with the [data-magnetic] attr,
   or automatically on all .button elements site-wide.

   Options (via data attributes on the element):
     data-magnetic-strength="0.3"   pull intensity 0–1 (default 0.3)
     data-magnetic-max="10"         max translate in px (default 10)

   You can also add [data-magnetic] to any element to opt-in.

   Automatically disabled for prefers-reduced-motion users.
   ─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var AUTO_SELECTOR = 'a.button:not(.slider-button), button.button:not(.slider-button), .btn:not(.slider-button)';
  var OPT_IN_ATTR   = 'data-magnetic';
  var DEFAULT_STRENGTH = 0.3;
  var DEFAULT_MAX      = 10;    /* px */
  var EASE_IN   = 'transform 0.12s ease';
  var EASE_OUT  = 'transform 0.55s cubic-bezier(0.23, 1, 0.32, 1)';

  function clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
  }

  function setup(el) {
    if (el._magInit) return;
    el._magInit = true;

    var strength = parseFloat(el.dataset.magneticStrength) || DEFAULT_STRENGTH;
    var maxPx    = parseFloat(el.dataset.magneticMax)      || DEFAULT_MAX;
    var raf      = null;
    var tx = 0;
    var ty = 0;

    el.addEventListener('mousemove', function (e) {
      var rect = el.getBoundingClientRect();
      /* Offset from centre of the element */
      var dx = e.clientX - (rect.left + rect.width  / 2);
      var dy = e.clientY - (rect.top  + rect.height / 2);

      tx = clamp(dx * strength, -maxPx, maxPx);
      ty = clamp(dy * strength, -maxPx, maxPx);

      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        el.style.transition = EASE_IN;
        el.style.transform  = 'translate(' + tx.toFixed(2) + 'px, ' + ty.toFixed(2) + 'px)';
      });
    });

    el.addEventListener('mouseleave', function () {
      if (raf) { cancelAnimationFrame(raf); raf = null; }
      el.style.transition = EASE_OUT;
      el.style.transform  = 'translate(0px, 0px)';
    });
  }

  function init(root) {
    root = root || document;

    /* Auto-target all .button elements */
    Array.prototype.forEach.call(root.querySelectorAll(AUTO_SELECTOR), setup);

    /* Opt-in elements via [data-magnetic] */
    Array.prototype.forEach.call(root.querySelectorAll('[' + OPT_IN_ATTR + ']'), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  window.MagneticHover = { refresh: init };
})();

;
/* ===== anchor-scroller.js ===== */
;/* ──────────────────────────────────────────────────────────────────────
   Anchor scroller — handles in-page nav links to elements with
   [data-anchor="..."] (Events / Player Profiles / Order of Merit).

   The cover-reveal and horizontal-scroll sections pin their content with
   transforms and sticky wrappers, so the browser's native scroll-to-id
   behaviour lands in the wrong place. This script intercepts the click,
   calculates the right scroll position, and runs a manual rAF tween
   (native scrollTo({behavior:'smooth'}) is intercepted by GSAP on this
   theme).
   ────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var HEADER_OFFSET = 90;

  /* ── Smooth scroll (manual rAF tween) ───────────────────────────── */
  var raf = null;
  function smoothScrollTo(targetY) {
    if (raf) cancelAnimationFrame(raf);
    var startY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var dist   = targetY - startY;
    if (Math.abs(dist) < 4) { window.scrollTo(0, targetY); return; }
    var dur    = Math.min(900, 320 + Math.abs(dist) * 0.3);
    var t0     = null;
    function step(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      var e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      window.scrollTo(0, Math.round(startY + dist * e));
      if (p < 1) raf = requestAnimationFrame(step);
      else raf = null;
    }
    raf = requestAnimationFrame(step);
  }

  function findScrollWrapper(el) {
    var hs = el.closest('[data-hscroll]');
    if (hs) return { kind: 'hs', wrapper: hs };
    var cv = el.closest('[data-cover-reveal]');
    if (cv) return { kind: 'cv', wrapper: cv };
    return null;
  }

  function indexOfPanel(wrapper, sel, target) {
    var panels = wrapper.querySelectorAll(sel);
    for (var i = 0; i < panels.length; i++) {
      if (panels[i] === target || panels[i].contains(target)) return i;
    }
    return 0;
  }

  function scrollToCV(wrapper, target) {
    var panels = wrapper.querySelectorAll('[data-cv-panel]');
    var wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
    if (!panels.length) {
      return smoothScrollTo(wrapperTop - HEADER_OFFSET);
    }
    /* With < 2 panels the cover-reveal JS bails out and never pins the
       wrapper or expands its height — the single panel renders inline at
       100vh, so just land at the section top. */
    if (panels.length < 2) {
      return smoothScrollTo(wrapperTop - HEADER_OFFSET);
    }
    var idx = indexOfPanel(wrapper, '[data-cv-panel]', target);
    var speed = parseInt(wrapper.dataset.cvSpeed, 10) || 200;
    var hold  = parseInt(wrapper.dataset.cvHold,  10) || 80;
    var total = hold + speed * (panels.length - 1) + hold;
    var vh    = window.innerHeight;

    if (window.innerWidth <= 768) {
      return smoothScrollTo(panels[idx].getBoundingClientRect().top + window.scrollY - HEADER_OFFSET);
    }

    var holdFrac = hold / total;
    var moveFrac = 1 - 2 * holdFrac;
    var n        = Math.max(1, panels.length - 1);
    var start    = holdFrac + (idx - 1) * (moveFrac / n);
    var end      = start    + (moveFrac / n);
    var progress = idx === 0 ? holdFrac * 0.5 : start + (end - start) * 0.7;
    var y        = wrapperTop + progress * total * vh / 100;
    smoothScrollTo(Math.max(0, y - HEADER_OFFSET / 2));
  }

  function scrollToHS(wrapper) {
    var y = wrapper.getBoundingClientRect().top + window.scrollY;
    smoothScrollTo(Math.max(0, y - HEADER_OFFSET / 2));
  }

  function scrollToTarget(target) {
    var ctx = findScrollWrapper(target);
    if (!ctx) {
      smoothScrollTo(Math.max(0, target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET));
      return;
    }
    if (ctx.kind === 'cv') scrollToCV(ctx.wrapper, target);
    else scrollToHS(ctx.wrapper);
  }

  function findTarget(name) {
    name = String(name || '').replace(/^#/, '');
    if (!name) return null;
    try {
      return document.querySelector('[data-anchor="' + CSS.escape(name) + '"]');
    } catch (e) {
      return document.querySelector('[data-anchor="' + name.replace(/"/g, '\\"') + '"]');
    }
  }

  function handleHash(name) {
    var target = findTarget(name);
    if (!target) return false;
    scrollToTarget(target);
    return true;
  }

  /* ── Re-bind on every nav link directly ─────────────────────────── */
  function bindLinks() {
    var links = document.querySelectorAll('a[href]');
    Array.prototype.forEach.call(links, function (a) {
      if (a.__anchorBound) return;
      var href = a.getAttribute('href') || '';
      var hashIdx = href.indexOf('#');
      if (hashIdx < 0) return;
      var hash = href.slice(hashIdx + 1);
      if (!hash) return;
      var pathBefore = href.slice(0, hashIdx);
      var samePath = pathBefore === '' || pathBefore === '/' ||
                     pathBefore === location.pathname;
      /* if href has no path (e.g. just "#events") AND we're not on the
         homepage where the target lives, rewrite to navigate home — that
         way nav links work from any page on the site */
      if (samePath && pathBefore === '' && !findTarget(hash) && location.pathname !== '/') {
        a.setAttribute('href', '/#' + hash);
        return;
      }
      if (!samePath) return;
      if (!findTarget(hash)) return;
      a.__anchorBound = true;
      /* attach in capture phase on the A itself so we beat magnetic-hover
         + btn-slide internal handlers.

         We've found that something in this theme silently swallows the
         in-page scroll (GSAP scroll-trigger? section-stack? — debugging
         the interaction proved fruitless). So instead of doing the scroll
         in-page, we force a full-page reload to the same URL with the
         hash attached. The bootHash path on the next page-load is rock
         solid — it actually scrolls — so this gives us a guaranteed UX
         at the cost of one extra navigation. */
      a.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var u = new URL(window.location.href);
        u.hash = '#' + hash;
        /* cache-bust so the browser treats this as a real navigation
           rather than a same-URL hash-only change */
        u.searchParams.set('_a', Date.now().toString(36).slice(-4));
        window.location.assign(u.pathname + u.search + u.hash);
      }, true);
    });
  }

  /* ── Init ───────────────────────────────────────────────────────── */
  function init() {
    bindLinks();
    /* clean any "_a=" parameter we used to force a full-page navigation */
    if (window.location.search.indexOf('_a=') !== -1) {
      try {
        var u = new URL(window.location.href);
        u.searchParams.delete('_a');
        history.replaceState(null, '', u.pathname + (u.search || '') + u.hash);
      } catch (e) {}
    }
    if (location.hash) {
      var tries = 0;
      function tryHash() {
        if (handleHash(location.hash) || tries > 12) return;
        tries++;
        setTimeout(tryHash, 120);
      }
      tryHash();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-bind when Shopify reloads sections in the editor */
  document.addEventListener('shopify:section:load', bindLinks);

  /* hashchange handler — use property assignment AS WELL AS addEventListener
     in case the theme's earlier scripts have wrapped addEventListener */
  var onHashChange = function () { handleHash(location.hash); };
  window.addEventListener('hashchange', onHashChange);
  var prevOnHashChange = window.onhashchange;
  window.onhashchange = function (e) {
    if (typeof prevOnHashChange === 'function') {
      try { prevOnHashChange.call(this, e); } catch (err) {}
    }
    onHashChange();
  };

  /* Belt-and-braces: re-walk the DOM every couple of seconds for the
     first few seconds, in case other scripts mutate the nav late */
  var passes = 0;
  var poll = setInterval(function () {
    bindLinks();
    if (++passes >= 6) clearInterval(poll);
  }, 500);
})();
/* v1781182185 */

;